import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Podcast, Loader2, RefreshCw, Users, Mic, Pencil, Download } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const STATUS = {
  pronto: { cls: "border-emerald-200 bg-emerald-100 text-emerald-700", label: "Podcast pronti" },
  in_generazione: { cls: "border-blue-200 bg-blue-100 text-blue-700", label: "Generazione in corso…" },
  da_aggiornare: { cls: "border-amber-200 bg-amber-100 text-amber-700", label: "Aggiornamento programmato…" },
  errore: { cls: "border-red-200 bg-red-100 text-red-700", label: "Errore generazione" },
};

export function PodcastAdmin({ code }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [partners, setPartners] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [editKind, setEditKind] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingScript, setSavingScript] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/admin/products/${code}/podcast`); setState(data); setSel(data.visibility?.authorized_partner_ids || []); }
    catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Auto-refresh mentre la generazione è in corso o programmata, così lo stato si aggiorna da solo.
  useEffect(() => {
    if (state?.status !== "in_generazione" && state?.status !== "da_aggiornare") return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [state?.status]); // eslint-disable-line

  const genera = async () => {
    setBusy(true);
    try { await api.post(`/admin/products/${code}/podcast/genera`); toast.success("Generazione avviata (~2 minuti). Aggiorna lo stato tra poco."); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const saveVisibility = async (all_partners, ids) => {
    try {
      const { data } = await api.post(`/admin/products/${code}/podcast/visibility`,
        { all_partners, authorized_partner_ids: ids });
      setState((s) => ({ ...s, visibility: data.visibility }));
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };

  const openDialog = async () => {
    if (!partners.length) {
      try { const { data } = await api.get("/admin/partners"); setPartners(data); } catch (e) { /* */ }
    }
    setSel(state?.visibility?.authorized_partner_ids || []);
    setDlgOpen(true);
  };

  const st = state?.status;
  const vis = state?.visibility || {};

  const openEdit = (kind) => {
    setEditKind(kind);
    setEditText(state?.podcasts?.[kind]?.script || "");
  };

  const saveScript = async () => {
    setSavingScript(true);
    try {
      await api.post(`/admin/products/${code}/podcast/${editKind}/script`, { script: editText });
      toast.success("Testo salvato. Rigenerazione audio avviata (~1 minuto). Aggiorna lo stato tra poco.");
      setEditKind(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingScript(false); }
  };

  const download = async (kind, label) => {
    setDownloading(kind);
    try {
      const { data: blob } = await api.get(`/products/${code}/podcast/${kind}/audio`, { responseType: "blob" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${code}_${kind}.mp3`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setDownloading(null); }
  };

  return (
    <div className="mt-4 rounded-lg border border-terracotta/20 bg-[#FBF6F3] p-4" data-testid={`podcast-admin-${code}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm font-semibold text-terracotta-hover">
          <Podcast className="h-4 w-4" /> Podcast formativi
        </span>
        <div className="flex items-center gap-2">
          {st && <Badge className={STATUS[st]?.cls}>{STATUS[st]?.label || st}</Badge>}
          <Button size="sm" variant="ghost" onClick={load} title="Aggiorna stato" data-testid={`podcast-refresh-${code}`} className="h-7 w-7 p-0 text-forest">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={genera} disabled={busy}
            data-testid={`podcast-genera-${code}`} className="h-7 gap-1.5 text-xs text-forest">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
            {st === "pronto" ? "Rigenera" : st === "in_generazione" ? "Riavvia" : "Genera podcast"}
          </Button>
        </div>
      </div>

      {st === "in_generazione" && (
        <p className="mt-2 text-xs text-muted-foreground">
          Generazione in corso… (~2 minuti). Lo stato si aggiorna da solo. Se resta bloccato, premi <b>Riavvia</b>.
        </p>
      )}
      {st === "da_aggiornare" && (
        <p className="mt-2 text-xs text-amber-700">
          Documenti aggiornati: l'audio verrà <b>rigenerato automaticamente</b> tra poco. Puoi anche premere <b>Rigenera</b> per farlo subito.
        </p>
      )}

      {st === "errore" && state?.error && <p className="mt-2 text-xs text-red-600">{state.error}</p>}

      {st === "pronto" && (
        <div className="mt-3 space-y-2 border-t border-terracotta/10 pt-3">
          <p className="text-xs text-muted-foreground">Tracce: Cliente finale · Partner introduttivo · Partner tecnico.</p>
          <div className="space-y-1.5">
            {Object.entries(state?.podcasts || {}).map(([kind, tr]) => (
              <div key={kind} className="rounded border border-terracotta/10 bg-white/60 px-3 py-2" data-testid={`podcast-admin-track-${code}-${kind}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-forest">
                    {tr.label}
                    {tr.edited && <Badge className="border-amber-200 bg-amber-100 text-[10px] text-amber-700">testo modificato</Badge>}
                  </span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => download(kind, tr.label)} disabled={downloading === kind}
                      className="flex items-center gap-1 text-xs text-terracotta-hover hover:text-terracotta disabled:opacity-50" data-testid={`podcast-admin-download-${code}-${kind}`}>
                      {downloading === kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Scarica MP3
                    </button>
                    <button onClick={() => openEdit(kind)} data-testid={`podcast-edit-${code}-${kind}`}
                      className="flex items-center gap-1 text-xs text-forest hover:text-terracotta-hover">
                      <Pencil className="h-3.5 w-3.5" /> Modifica testo
                    </button>
                  </div>
                </div>
                <audio controls preload="none" className="mt-2 h-9 w-full" data-testid={`podcast-admin-audio-${code}-${kind}`}
                  src={`${API}/products/${code}/podcast/${kind}/audio`} />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-sm text-forest">Visibile a <b>tutti i partner</b></span>
            <Switch checked={!!vis.all_partners} data-testid={`podcast-allpartners-${code}`}
              onCheckedChange={async (v) => { if (await saveVisibility(v, vis.authorized_partner_ids || [])) toast.success(v ? "Podcast visibili a tutti i partner" : "Visibilità ristretta"); }} />
          </div>
          {!vis.all_partners && (
            <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" onClick={openDialog} data-testid={`podcast-access-${code}`} className="h-7 gap-1.5 text-xs text-forest">
                  <Users className="h-3.5 w-3.5" /> Gestisci accessi ({(vis.authorized_partner_ids || []).length})
                </Button>
              </DialogTrigger>
              <DialogContent data-testid={`podcast-access-dialog-${code}`}>
                <DialogHeader><DialogTitle>Partner autorizzati ai podcast</DialogTitle></DialogHeader>
                <p className="text-xs text-muted-foreground">Admin, Operatore e collaboratori A-con-A vedono sempre i podcast. Qui puoi abilitare altri partner.</p>
                <div className="max-h-72 space-y-1.5 overflow-y-auto py-2">
                  {partners.map((p) => (
                    <label key={p.id} className="flex items-center gap-2.5 rounded px-1 py-1 text-sm hover:bg-sage/40">
                      <Checkbox checked={sel.includes(p.id)} data-testid={`podcast-partner-${code}-${p.codice_collab}`}
                        onCheckedChange={() => setSel((s) => s.includes(p.id) ? s.filter((x) => x !== p.id) : [...s, p.id])} />
                      <span className="text-forest">{p.ragione_sociale || p.nome || p.codice_collab}</span>
                      <span className="text-xs text-muted-foreground">{p.codice_collab}</span>
                    </label>
                  ))}
                  {partners.length === 0 && <p className="text-sm text-muted-foreground">Nessun partner.</p>}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDlgOpen(false)}>Annulla</Button>
                  <Button data-testid={`podcast-access-save-${code}`} className="bg-forest hover:bg-forest/90"
                    onClick={async () => { if (await saveVisibility(false, sel)) { toast.success("Accessi aggiornati"); setDlgOpen(false); } }}>
                    Salva
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}

      <Dialog open={!!editKind} onOpenChange={(o) => { if (!o) setEditKind(null); }}>
        <DialogContent className="max-w-2xl" data-testid={`podcast-edit-dialog-${code}`}>
          <DialogHeader>
            <DialogTitle>Modifica testo · {state?.podcasts?.[editKind]?.label || ""}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Correggi il testo dello script. Salvando, l'audio MP3 verrà rigenerato <b>esattamente da questo testo</b> (senza AI).
          </p>
          <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={14}
            data-testid={`podcast-edit-textarea-${code}`} className="text-sm leading-relaxed" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditKind(null)} disabled={savingScript}>Annulla</Button>
            <Button onClick={saveScript} disabled={savingScript || editText.trim().length < 20}
              data-testid={`podcast-edit-save-${code}`} className="gap-1.5 bg-terracotta hover:bg-terracotta-hover">
              {savingScript ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />} Salva e rigenera audio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
